'use client'

import { useAnalyticsImpressions } from 'analytics/contexts/AnalyticsImpressionsProvider'
import type { Item } from 'analytics/utils/getEECItems/getEECItems'
import { Slideshow } from 'fukku/Slideshow'
import { useIntersectionObserver } from 'hooks/useIntersectionObserver/useIntersectionObserver'
import { PdpClientLink } from 'links/pdp/client'
import Image from 'next/image'
import { dataTestIds } from 'product-card/constants/dataTestIds'
import { useProductCardConsumerLayoutContext } from 'product-card/hooks/useProductCardConsumerLayoutContext'
import { useProductCardContext } from 'product-card/hooks/useProductCardContext'
import { useProductCardHoverContext } from 'product-card/hooks/useProductCardHoverContext'
import { useProductCardImagesSizesContext } from 'product-card/hooks/useProductCardImagesSizesContext'
import { generateImagesSizes } from 'product-card/utils/generateImagesSizes'
import { getProductLinkLabel } from 'product-card/utils/getProductLinkLabel/getProductLinkLabel'
import { getSortedImages } from 'product-card/utils/getSortedImages'
import { sendSwipeImagesEvent } from 'product/analytics/sendSwipeImagesEvent'
import type { Product, ProductImageType } from 'product/types'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useMatchMedia } from 'utils/matchMedia/hooks'

import styles from './ProductImage.module.scss'
import fukkuStyles from 'fukku/styles/classes.module.scss'
import mq from 'utils/matchMedia/styles/MatchMediaQueries.module.scss'

interface ProductImageProps {
	product: Product
	imageSortCriteria: ProductImageType[]
	priority?: boolean
	showAsFeatured?: boolean
}

const IMPRESSION_THRESHOLD = 0.5

export const ProductImage = ({
	product,
	imageSortCriteria,
	priority,
	showAsFeatured,
}: ProductImageProps) => {
	const [islessthanlarge] = useMatchMedia([mq.islessthanlarge])
	const {
		lookId,
		selectedColorId,
		dataTestId,
		productId,
		location,
		productListId,
	} = useProductCardContext()
	const { isTwoColumns } = useProductCardConsumerLayoutContext()
	const { imagesSizes } = useProductCardImagesSizesContext()
	const { addImpression } = useAnalyticsImpressions()
	const productImageRef = useRef<HTMLImageElement | null>(null)
	const entry = useIntersectionObserver(productImageRef, {
		threshold: IMPRESSION_THRESHOLD,
		freezeOnceVisible: true,
	})

	const { isHovered } = useProductCardHoverContext()

	const sortedImages = useMemo(
		() =>
			getSortedImages({
				product,
				imageSortCriteria,
				showAsFeatured,
				isMobile: islessthanlarge,
				colorId: selectedColorId,
				lookId: lookId.current,
			}),
		[
			product,
			imageSortCriteria,
			showAsFeatured,
			islessthanlarge,
			selectedColorId,
			lookId,
		]
	)

	const isMobileTwoColumnSize = isTwoColumns && islessthanlarge
	const isSingleImage =
		sortedImages && (sortedImages.length === 1 || isMobileTwoColumnSize)
	const isImagesSlider =
		sortedImages && sortedImages.length > 1 && !isMobileTwoColumnSize
	const currentImages =
		isHovered || islessthanlarge ? sortedImages : sortedImages.slice(0, 1)

	const sizes = useMemo(
		() =>
			generateImagesSizes(
				!showAsFeatured ? imagesSizes.regular : imagesSizes.featured
			),
		[imagesSizes]
	)

	const selectedColor = product.colors.find(
		(color) => color.id === selectedColorId
	)

	const countOfImages = currentImages?.length

	const swipeActionHandler = useCallback(() => {
		sendSwipeImagesEvent({
			productId: product.id,
			colorId: selectedColorId,
			count: countOfImages,
			location,
		})
	}, [location, product.id, selectedColorId, countOfImages])

	useEffect(() => {
		if (entry?.isIntersecting) {
			const item: Item = {
				colorId: selectedColorId,
				lookId: lookId.current,
				listId: productListId,
				quantity: 1,
				productId,
			}
			addImpression(item)
		}
	}, [entry?.isIntersecting, productId])

	if (isSingleImage) {
		return (
			<div className={styles.productImage} data-testid='productCard.image'>
				<div
					className={styles.imageWrapper}
					data-testid='productCard.image.wrapper'
				>
					<PdpClientLink
						product={product}
						lookId={lookId.current}
						colorId={selectedColorId}
						linkProps={{
							className: styles.link,
							linkProps: {
								'aria-label': getProductLinkLabel(product, selectedColorId),
							},
						}}
						location=''
					>
						<Image
							data-testid={dataTestIds.plpProductCardImage(dataTestId, 0)}
							alt={sortedImages[0].alt}
							src={sortedImages[0].img}
							sizes={sizes}
							priority={priority}
							ref={productImageRef}
							fill
						/>
						<span className={fukkuStyles.srOnly}>
							{product.name} {selectedColor?.label} ({product.reference})
						</span>
					</PdpClientLink>
				</div>
			</div>
		)
	}

	if (isImagesSlider) {
		return (
			<div className={styles.productImage} data-testid='productCard.images'>
				<PdpClientLink
					product={product}
					lookId={lookId.current}
					colorId={selectedColorId}
					linkProps={{
						linkProps: {
							'aria-label': getProductLinkLabel(product, selectedColorId),
						},
					}}
				>
					<Slideshow
						className={styles.slideShow}
						isTouchable={islessthanlarge}
						dataTestId={dataTestIds.plpProductCardSlideshow(dataTestId)}
						hasPreload
						onSwipe={swipeActionHandler}
					>
						{currentImages?.map((image, index) => (
							<div key={image.alt} className={styles.imageWrapper}>
								<Image
									data-testid={dataTestIds.plpProductCardImage(
										dataTestId,
										index
									)}
									alt={image.alt}
									src={image.img}
									sizes={sizes}
									priority={priority && index === 0}
									ref={productImageRef}
									fill
								/>
							</div>
						))}
					</Slideshow>
				</PdpClientLink>
			</div>
		)
	}

	return null
}
